import type React from "react";
import {
  AccountTreeOutlined,
  Add,
  AdminPanelSettings,
  ArrowBackRounded,
  ArrowDownwardRounded,
  ArrowLeftRounded,
  ArrowRightRounded,
  ArrowUpwardRounded,
  AutoAwesome,
  BarChart,
  Biotech,
  Bolt,
  Brush,
  Build,
  Calculate,
  Handyman,
  CallMerge,
  Cancel,
  CheckCircle,
  Checklist,
  CheckRounded,
  ChevronLeftRounded,
  ChevronRightRounded,
  CloseRounded,
  Construction,
  ContentCopy,
  DarkMode,
  DataObject,
  Delete,
  DisabledByDefault,
  DirectionsRun,
  Download,
  Edit,
  Error,
  Event,
  Folder,
  FolderOpen,
  GridView,
  Groups,
  Home,
  ImageOutlined,
  Inbox,
  IndeterminateCheckBox,
  InfoOutlined,
  Key,
  Keyboard,
  KeyboardHide,
  KeyboardVoice,
  Language,
  Layers,
  LightMode,
  LinkOutlined,
  List,
  Lock,
  LockOpen,
  Logout,
  MailOutlined,
  MessageOutlined,
  Mic,
  MicOff,
  MoreVertOutlined,
  MusicNote,
  MyLocation,
  Notifications,
  Numbers,
  OpenInNew,
  PauseRounded,
  PendingOutlined,
  Person,
  Phone,
  PhoneForwarded,
  PlayArrowRounded,
  QuestionMarkOutlined,
  RadioButtonUnchecked,
  RecordVoiceOver,
  Refresh,
  Repeat,
  ReplayRounded,
  RocketLaunchOutlined,
  Save,
  School,
  SearchOutlined,
  SendOutlined,
  ShieldOutlined,
  ShowChartOutlined,
  SmartToy,
  SoupKitchen,
  Source,
  Stop,
  TextFields,
  TextSnippet,
  TrendingUpOutlined,
  Tune,
  Upload,
  VisibilityOffOutlined,
  VisibilityOutlined,
  VolumeMute,
  VolumeUpOutlined,
  WarningAmber,
  StarOutline,
  SwitchAccessShortcutAdd,
  Menu,
  AccessTime,
  Headphones,
  Input,
  Output,
  DataArray,
} from "@mui/icons-material";

import { ReactComponent as PythonLogo } from "programming-languages-logos/src/python/python.svg";
import { ReactComponent as JavascriptLogo } from "programming-languages-logos/src/javascript/javascript.svg";

import { memoize } from "../../utils/performance";
import { Style } from "../../utils/types";
import { CodeIcon, DatabaseIcon, DocumentTextIcon, CogIcon, PhoneOutgoingIcon, PhoneIncomingIcon } from "@heroicons/react/outline";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { SvgIconTypeMap } from "@mui/material";
import clsx from "clsx";

const VoiceRunIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <img
    src="/images/logos/logo.png"
    alt="VoiceRun"
    className={className}
    style={style}
  />
);

const MockVoiceRunIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <img
    src="/images/logos/logo.png"
    alt="VoiceRun Mock"
    className={clsx(className, "invert brightness-50")}
    style={style}
  />
);

const TwilioIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    className={className}
    style={style}
    viewBox="0 0 256 256"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
    xmlnsXlink="http://www.w3.org/1999/xlink"
    preserveAspectRatio="xMidYMid"
  >
    <g>
      <path
        d="M128,0 C198.656,0 256,57.344 256,128 C256,198.656 198.656,256 128,256 C57.344,256 0,198.656 0,128 C0,57.344 57.344,0 128,0 Z M128,33.792 C75.776,33.792 33.792,75.776 33.792,128 C33.792,180.224 75.776,222.208 128,222.208 C180.224,222.208 222.208,180.224 222.208,128 C222.208,75.776 180.224,33.792 128,33.792 Z M159.744,133.12 C174.448029,133.12 186.368,145.039971 186.368,159.744 C186.368,174.448029 174.448029,186.368 159.744,186.368 C145.039971,186.368 133.12,174.448029 133.12,159.744 C133.12,145.039971 145.039971,133.12 159.744,133.12 Z M96.256,133.12 C110.960029,133.12 122.88,145.039971 122.88,159.744 C122.88,174.448029 110.960029,186.368 96.256,186.368 C81.5519708,186.368 69.632,174.448029 69.632,159.744 C69.632,145.039971 81.5519708,133.12 96.256,133.12 Z M159.744,69.632 C174.448029,69.632 186.368,81.5519708 186.368,96.256 C186.368,110.960029 174.448029,122.88 159.744,122.88 C145.039971,122.88 133.12,110.960029 133.12,96.256 C133.12,81.5519708 145.039971,69.632 159.744,69.632 Z M96.256,69.632 C110.960029,69.632 122.88,81.5519708 122.88,96.256 C122.88,110.960029 110.960029,122.88 96.256,122.88 C81.5519708,122.88 69.632,110.960029 69.632,96.256 C69.632,81.5519708 81.5519708,69.632 96.256,69.632 Z"
        fill="#F12E45"
      />
    </g>
  </svg>
);

const TelnyxIcon = ({ className, style }: { className?: string; style?: React.CSSProperties }) => (
  <svg
    className={className}
    style={style}
    viewBox="0 2 35 28"
    version="1.1"
  >
    <path
      d="M33.843 28.9843C34.42 27.9451 34.5842 26.7302 34.3035 25.5775C34.223 25.2589 34.1118 24.9486 33.9713 24.6511C33.927 24.5535 33.8765 24.4586 33.8203 24.3672L29.2453 15.8129H24.5645L29.3509 24.7781C29.5012 25.0387 29.5803 25.3335 29.5803 25.6335C29.5803 25.9335 29.5012 26.2284 29.3509 26.4889C29.2045 26.7324 28.9968 26.934 28.748 27.0742C28.4992 27.2143 28.2179 27.2881 27.9316 27.2883H22.4732C22.4128 28.123 22.1509 28.9311 21.7095 29.6448C21.2681 30.3585 20.6601 30.957 19.9365 31.3899H29.97C30.6399 31.3459 31.2918 31.1571 31.8801 30.8371C32.7103 30.4324 33.3953 29.7858 33.843 28.9843Z"
      fill="#00E3AA"
    ></path>
    <path
      d="M8.18188 10.2171H12.8627L15.1955 5.92124C15.3929 5.55049 15.6888 5.24011 16.0514 5.0236C16.4139 4.80709 16.8293 4.69266 17.2528 4.69266C17.6763 4.69266 18.0917 4.80709 18.4542 5.0236C18.8168 5.24011 19.1127 5.55049 19.3101 5.92124L21.575 10.2171H26.2558L22.9339 4.00118C22.3831 2.97418 21.5602 2.11498 20.5533 1.51577C19.5465 0.916566 18.3937 0.599976 17.2188 0.599976C16.0439 0.599976 14.8912 0.916566 13.8843 1.51577C12.8775 2.11498 12.0545 2.97418 11.5037 4.00118L8.18188 10.2171Z"
      fill="#00E3AA"
    ></path>
    <path
      d="M11.9566 15.0658C12.0057 14.2346 12.2572 13.4275 12.6897 12.7133C13.1222 11.9991 13.7229 11.399 14.4405 10.9642H5.38086V15.0658H11.9566Z"
      fill="#00E3AA"
    ></path>
    <path
      d="M12.7039 15.3647V26.5712H14.8329C15.2769 26.5663 15.707 26.4175 16.0574 26.1476C16.4079 25.8778 16.6593 25.5017 16.7731 25.077C16.8189 24.914 16.8418 24.7456 16.8411 24.5764V17.8525C16.843 17.1226 17.1359 16.4228 17.6561 15.9053C18.1762 15.3877 18.8817 15.0941 19.6193 15.0882H29.0942V10.9642H17.1431C15.965 10.9662 14.8359 11.4307 14.0036 12.2557C13.1713 13.0808 12.7039 14.1989 12.7039 15.3647Z"
      fill="#00E3AA"
    ></path>
    <path
      d="M0.465884 24.6511C0.325416 24.9487 0.214176 25.2589 0.133699 25.5775C-0.146759 26.7327 0.0174125 27.9498 0.594228 28.9918C1.04312 29.7905 1.72803 30.4344 2.55714 30.8371C3.14541 31.1572 3.79736 31.3459 4.4672 31.39H17.2186C18.42 31.39 19.5721 30.9177 20.4216 30.077C21.2711 29.2364 21.7484 28.0962 21.7484 26.9074V15.8054H19.5892C19.0584 15.819 18.5537 16.0362 18.1817 16.4112C17.8098 16.7863 17.5998 17.2897 17.5961 17.8151V24.539C17.5941 25.2689 17.2997 25.9682 16.7774 26.4836C16.2552 26.999 15.5478 27.2884 14.8102 27.2884H6.54336C6.25709 27.2881 5.97579 27.2143 5.72699 27.0742C5.47819 26.9341 5.27043 26.7324 5.12402 26.489C4.9738 26.227 4.89483 25.931 4.89483 25.6298C4.89483 25.3286 4.9738 25.0326 5.12402 24.7706L9.91051 15.8054H5.19197L0.616877 24.3597C0.564029 24.4643 0.511182 24.554 0.465884 24.6511Z"
      fill="#00E3AA"
    ></path>
  </svg>
);

type IconComponent = React.ElementType;

const icons: Record<string, IconComponent> = {
  // Navigation / arrows
  "arrow-back": ArrowBackRounded,
  "arrow-down": ArrowDownwardRounded,
  "arrow-left": ArrowLeftRounded,
  "arrow-right": ArrowRightRounded,
  "arrow-up": ArrowUpwardRounded,

  // Chevrons (rotated via styles below)
  "chevron-down": ChevronRightRounded,
  "chevron-left": ChevronLeftRounded,
  "chevron-right": ChevronRightRounded,
  "chevron-up": ChevronLeftRounded,

  // Common actions
  add: Add,
  trash: Delete,
  edit: Edit,
  close: CloseRounded,
  copy: ContentCopy,
  download: Download,
  upload: Upload,
  open: OpenInNew,
  search: SearchOutlined,
  "more-vertical": MoreVertOutlined,

  // Status / feedback
  check: CheckRounded,
  "check-circle": CheckCircle,
  checklist: Checklist,
  pending: PendingOutlined,
  loader: Refresh,
  refresh: ReplayRounded,
  circle: RadioButtonUnchecked,
  "close-circle": Cancel,
  "skip-forward": ArrowRightRounded,
  "warning-amber": WarningAmber,
  error: Error,
  info: InfoOutlined,
  disabled: DisabledByDefault,

  // Media
  play: PlayArrowRounded,
  pause: PauseRounded,
  stop: Stop,
  square: Stop,
  mic: Mic,
  "mic-off": MicOff,
  "volume-up": VolumeUpOutlined,
  mute: VolumeMute,
  music: MusicNote,

  // Auth / visibility
  lock: Lock,
  unlock: LockOpen,
  visibility: VisibilityOutlined,
  "visibility-off": VisibilityOffOutlined,

  // Theme
  moon: DarkMode,
  notification: Notifications,
  person: Person,
  phone: Phone,
  plus: Add,
  python: PythonLogo as unknown as OverridableComponent<SvgIconTypeMap<{}, "svg">>,
  recipe: SoupKitchen,
  remix: SwitchAccessShortcutAdd,
  repeat: Repeat,
  rocket: RocketLaunchOutlined,
  run: DirectionsRun,
  save: Save,
  school: School,
  send: SendOutlined,
  settings: CogIcon,
  source: Source,
  sparkles: AutoAwesome,
  star: StarOutline,
  sun: LightMode,

  // Messaging
  message: MessageOutlined,

  // People / agents
  bot: SmartToy,
  user: Person,
  users: Groups,
  "voice-over": RecordVoiceOver,

  // Domain / pages / configs
  admin_panel_settings: AdminPanelSettings,
  home: Home,
  grid: GridView,
  layers: Layers,
  link: LinkOutlined,
  list: List,
  key: Key,
  logout: Logout,
  inbox: Inbox,
  event: Event,
  "phone-forward": PhoneForwarded,
  "phone-merge": CallMerge,
  "phone-inbound": PhoneIncomingIcon,
  "phone-outbound": PhoneOutgoingIcon,
  shield: ShieldOutlined,
  snippet: TextSnippet,
  testing: Biotech,
  brush: Brush,
  zap: Bolt,
  mail: MailOutlined,
  invite: MailOutlined,

  // Data / dev / config
  code: DataObject,
  "code-block": CodeIcon,
  database: DatabaseIcon,
  file: DocumentTextIcon,
  folder: Folder,
  folder_open: FolderOpen,
  image: ImageOutlined,
  "account-tree": AccountTreeOutlined,
  "git-branch": AccountTreeOutlined,
  "bar-chart-2": BarChart,
  activity: ShowChartOutlined,
  "pie-chart": ShowChartOutlined,
  funnel: ShowChartOutlined,
  target: MyLocation,
  "trending-up": TrendingUpOutlined,
  variants: Tune,
  text: TextFields,
  type: TextFields,
  number: Numbers,
  decimal: Calculate,
  indeterminate: IndeterminateCheckBox,
  keyboard: Keyboard,
  "keyboard-off": KeyboardHide,
  "add-voice": KeyboardVoice,
  language: Language,
  menu: Menu,
  clock: AccessTime,
  headphones: Headphones,
  tool: Handyman,
  construction: Construction,
  input: Input,
  output: Output,
  "data-array": DataArray,

  // Provider logos
  voicerun: VoiceRunIcon,
  "mock-voicerun": MockVoiceRunIcon,
  twilio: TwilioIcon,
  telnyx: TelnyxIcon,

  // Language logos
  javascript: JavascriptLogo,

  // Fallback-ish / back-compat
  "question-mark": QuestionMarkOutlined,
  assessment: Checklist,
};

const styles: Record<string, Style> = {
  "chevron-down": { transform: "rotate(90deg)" },
  "chevron-up": { transform: "rotate(90deg)" },
};

interface IconProps {
  name: string;
  size?: number;
  className?: string;
  title?: string;
}

export const Icon = memoize(({ name, size = 24, className, title }: IconProps) => {
  const Component = icons[name] || QuestionMarkOutlined;

  return (
    <Component
      className={className}
      style={{
        fontSize: size,
        width: size,
        height: size,
        ...styles[name],
      }}
      titleAccess={title}
      aria-label={title}
      role={title ? "img" : undefined}
    />
  );
});
